import React, { useState, useRef, useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom'
import Home from './pages/Home'
import Pricing from './pages/Pricing'
import About from './pages/About'
import FAQ from './pages/FAQ'
import { Privacy, Terms } from './pages/Legal'
import Accessibility from './pages/Accessibility'
import Onboard from './pages/Onboard'
import Dashboard from './pages/Dashboard'
import GrantSearch from './pages/GrantSearch'
import FoundationSearch from './pages/FoundationSearch'
import Cultivate from './pages/Cultivate'
import CorpusSearch from './pages/CorpusSearch'
import FunderProfile from './pages/FunderProfile'
import Preferences from './pages/Preferences'
import Account from './pages/Account'
import CheckInbox from './pages/CheckInbox'
import ConfirmResult from './pages/ConfirmResult'
import SignIn from './pages/SignIn'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import { BillingSuccess, BillingCancel } from './pages/BillingResult'
import Unsubscribe from './pages/Unsubscribe'
import Feedback from './pages/Feedback'
import Plans from './pages/Plans'
import { logout as apiLogout } from './api'
import { hasCachedSession, getOrgName, clearSession } from './session'
import { PublicConfigProvider } from './publicConfig'
import { hostRedirect } from './hosts'
import { hereRole, ORIGINS } from './twoHost'
import ScrollToTop from './components/ScrollToTop'
import Spinner from './components/Spinner'

// Lazy: the operator console is code that no customer ever runs, so it is
// split out of the main bundle rather than shipped to every visitor. The
// route is not a secret (the backend 404s non-admins), but there is no
// reason to hand everyone the code either.
const Admin = lazy(() => import('./pages/Admin'))

// The two halves of the corpus are DIFFERENT KINDS OF THING and the nav must not
// blur them (CLAUDE.md): federal rows are real opportunities with deadlines and
// CFDA numbers; foundations are leads derived from 990 filings, with no RFP and
// no opportunity deadline. Hence "Foundations", not "Foundation Grants" — a
// tester who opens a page promising grants and finds nothing to apply to
// concludes the product is broken.
//
// Foundations ships dark until its page exists, so this file can deploy at any
// point without advertising a route that is not built. Flip to true in the same
// commit that adds the page.
// Enabled 2026-08-11 (homelab-kb #60 Phase 1) — searches the client's own
// matched foundations, which needed no change to corpus authorization.
const FOUNDATIONS_ENABLED = true

const GRANT_DB_LINKS = [
  { to: '/grants/federal', label: 'Federal Grants', enabled: true },
  { to: '/grants/foundations', label: 'Foundations', enabled: FOUNDATIONS_ENABLED },
  // Corpus search (#60 Phase 2a, ADR-0012). Shown to EVERY tier on purpose:
  // the badge says Pro, and a Growth client who opens it meets the upgrade
  // prompt. Hiding it entirely would leave the tier's main selling point
  // invisible to exactly the people meant to buy it.
  {
    to: '/grants/all-foundations',
    label: 'All Foundations',
    badge: 'Pro',
    enabled: FOUNDATIONS_ENABLED,
  },
]

// One row of GRANT_DB_LINKS. Shared by the header's Grant Database dropdown
// (>= sm) and the profile dropdown's mobile section (< sm) so a link can never
// exist in one and be missing from the other.
function GrantDbMenuItem({ link, currentPath, onNavigate }) {
  return (
    <Link
      to={link.to}
      role="menuitem"
      onClick={onNavigate}
      className={`block px-4 py-2 text-sm hover:bg-gray-50 ${
        currentPath === link.to ? 'font-semibold text-brand-900' : ''
      }`}
    >
      <span className="flex items-center justify-between gap-2">
        {link.label}
        {link.badge && (
          <span className="text-[10px] font-semibold uppercase tracking-wide
            px-1.5 py-0.5 rounded bg-brand-100 text-brand-500">
            {link.badge}
          </span>
        )}
      </span>
    </Link>
  )
}

function GrantDatabaseMenu() {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const location = useLocation()

  // Close on outside click (same pattern as ProfileMenu).
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  // Close on route change, so following a link never leaves the menu hanging open.
  useEffect(() => { setOpen(false) }, [location.pathname])

  const links = GRANT_DB_LINKS.filter(l => l.enabled)
  const active = location.pathname.startsWith('/grants')

  return (
    <div className="relative hidden sm:block" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center gap-1 hover:opacity-80 transition-opacity ${
          active ? 'opacity-100 font-semibold' : 'opacity-70'
        }`}
      >
        Grant Database
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div role="menu" className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 text-gray-700 z-20">
          {links.map(link => (
            <GrantDbMenuItem
              key={link.to}
              link={link}
              currentPath={location.pathname}
              onNavigate={() => setOpen(false)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ProfileMenu({ orgName, onSignOut }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const location = useLocation()

  // Close on outside click.
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  // Close on route change (same reason as GrantDatabaseMenu): the mobile grant
  // links below navigate from inside this menu.
  useEffect(() => { setOpen(false) }, [location.pathname])

  const initial = (orgName || '?').trim().charAt(0).toUpperCase()
  const grantLinks = GRANT_DB_LINKS.filter(l => l.enabled)

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white text-sm font-semibold">
          {initial}
        </span>
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 text-gray-700 z-20">
          {orgName && (
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs text-gray-500">Signed in as</p>
              <p className="text-sm font-medium text-gray-900 truncate">{orgName}</p>
            </div>
          )}
          {/* Below `sm` the header's Grant Database dropdown is hidden, so this
              is the ONLY route to the corpus pages on a phone. Rendered from
              GRANT_DB_LINKS rather than restated, so a page added there shows
              up on both breakpoints in the same commit. Dashboard needs no
              equivalent — the wordmark links to it at every width. */}
          <div className="sm:hidden border-b border-gray-100 py-1">
            <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Grant database
            </p>
            {grantLinks.map(link => (
              <GrantDbMenuItem
                key={link.to}
                link={link}
                currentPath={location.pathname}
                onNavigate={() => setOpen(false)}
              />
            ))}
          </div>
          <Link to="/account" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm hover:bg-gray-50">
            Account settings
          </Link>
          <Link to="/account" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm hover:bg-gray-50">
            Billing
          </Link>
          <Link to="/plans" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm hover:bg-gray-50">
            Plans
          </Link>
          <Link to="/preferences" onClick={() => setOpen(false)} className="block px-4 py-2 text-sm hover:bg-gray-50">
            Preferences
          </Link>
          <button
            onClick={() => { setOpen(false); onSignOut() }}
            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

function NavBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const orgName = getOrgName()

  // Don't show the authenticated app nav on public pages. Marketing pages bring
  // their own PublicNav (via PublicLayout); auth + onboarding + email-confirmation
  // pages are chrome-less.
  const PUBLIC_PATHS = [
    '/', '/signup', '/pricing', '/about', '/faq', '/privacy', '/terms',
    '/accessibility',
    '/check-inbox', '/confirm-result', '/signin', '/forgot-password', '/reset-password',
    '/unsubscribe', '/feedback',
  ]
  if (PUBLIC_PATHS.includes(location.pathname)) return null

  async function signOut() {
    // Clear the server cookie first, then the local cache, then redirect.
    try { await apiLogout() } catch (_) { /* clear locally regardless */ }
    clearSession()
    navigate('/signin')
  }

  return (
    <header className="bg-brand-900 text-white shadow-md">
      <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/dashboard" className="text-lg font-bold tracking-tight hover:opacity-90">
          Praenum
        </Link>
        <nav className="flex items-center gap-6 text-sm">
          {/* Hidden on mobile: the logo already links to the dashboard and the
              profile menu covers Preferences, so the text links only add crowding. */}
          <Link
            to="/dashboard"
            className={`hidden sm:inline hover:opacity-80 transition-opacity ${
              location.pathname === '/dashboard' ? 'opacity-100 font-semibold' : 'opacity-70'
            }`}
          >
            Dashboard
          </Link>
          <GrantDatabaseMenu />
          <Link
            to="/preferences"
            className={`hidden sm:inline hover:opacity-80 transition-opacity ${
              location.pathname === '/preferences' ? 'opacity-100 font-semibold' : 'opacity-70'
            }`}
          >
            Preferences
          </Link>
          <ProfileMenu orgName={orgName} onSignOut={signOut} />
        </nav>
      </div>
    </header>
  )
}

function RequireAuth({ children }) {
  // Optimistic gate on the cached client summary. The HttpOnly auth cookie is
  // the real authority — if it's missing/expired, the first API call returns
  // 401, the API layer clears the cache, and the next render redirects here.
  const location = useLocation()
  if (hasCachedSession()) return children
  // Carry the destination through sign-in, so a funder-profile link in a digest
  // still lands on that funder after signing in (#210: the move to the app host
  // signs everyone out at once). Router state rather than a `?next=` parameter,
  // so no link can set it; SignIn checks it with safeReturnPath all the same.
  return <Navigate to="/signin" replace state={{ from: location.pathname + location.search }} />
}

// The app's session lives on the app host, so praenum.com cannot see it: there
// `/` is always the marketing homepage. On the app host `/` is the dashboard,
// which sends a signed-out visitor on to /signin. Locally, one host serves
// both, and a cached session goes to the dashboard as it always did.
function HomeOrDashboard() {
  const here = hereRole()
  if (here === 'app' || (here === null && hasCachedSession())) {
    return <Navigate to="/dashboard" replace />
  }
  return <Home />
}

// An unknown path goes to the front page of whichever host it was asked of.
function CatchAll() {
  if (hereRole() === 'app') return <Navigate to="/dashboard" replace />
  return <Navigate to="/" replace />
}

// A page on the wrong host is replaced by the same path on the right one
// (src/hosts.js). nginx already 301s a full page load; this catches the
// navigation nginx never sees, and an old bundle's links. A real page load,
// not a router navigation, because the point is to change origin.
function HostGuard({ children }) {
  const location = useLocation()
  const target = hostRedirect(location, hereRole(), ORIGINS)
  useEffect(() => {
    if (target) window.location.replace(target)
  }, [target])
  return target ? null : children
}

export default function App() {
  return (
    <BrowserRouter>
      <HostGuard>
      <PublicConfigProvider>
      <ScrollToTop />
      <div className="min-h-screen bg-gray-50">
        {/* Keyboard users land here first and can jump past the nav (WCAG 2.4.1).
            Visually hidden until focused. */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2
                     focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm
                     focus:font-medium focus:text-brand-500 focus:shadow-lg
                     focus:ring-2 focus:ring-brand-500"
        >
          Skip to main content
        </a>
        <NavBar />
        {/* The single <main> landmark for the whole app. PublicLayout renders a
            plain wrapper so public pages don't nest a second one inside this. */}
        <main id="main-content" tabIndex={-1}>
          <Routes>
            {/* Public marketing pages */}
            <Route path="/" element={<HomeOrDashboard />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/accessibility" element={<Accessibility />} />
            {/* Signup wizard (moved off `/`) */}
            <Route path="/signup" element={<Onboard />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/check-inbox" element={<CheckInbox />} />
            <Route path="/confirm-result" element={<ConfirmResult />} />
            {/* Digest-email link targets — public, confirm-button flows
                (prefetch-safe: nothing happens on GET) */}
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/dashboard" element={
              <RequireAuth><Dashboard /></RequireAuth>
            } />
            {/* /grants was the single search page before the corpus was split
                into two. Kept as a redirect so existing bookmarks survive. */}
            <Route path="/grants" element={<Navigate to="/grants/federal" replace />} />
            <Route path="/grants/foundations" element={
              <RequireAuth><FoundationSearch /></RequireAuth>
            } />
            <Route path="/grants/all-foundations" element={
              <RequireAuth><CorpusSearch /></RequireAuth>
            } />
            <Route path="/grants/federal" element={
              <RequireAuth><GrantSearch /></RequireAuth>
            } />
            {/* The cultivate lane (ADR-0052). A route of its own rather than a
                tab on /grants/foundations: these funders have no application,
                and a page that looked like the lead list would unmake the one
                distinction the surface exists to draw. */}
            <Route path="/cultivate" element={
              <RequireAuth><Cultivate /></RequireAuth>
            } />
            <Route path="/funders/:funderId" element={
              <RequireAuth><FunderProfile /></RequireAuth>
            } />
            <Route path="/preferences" element={
              <RequireAuth><Preferences /></RequireAuth>
            } />
            {/* Operator console. RequireAuth only gets the caller as far as the
                fetch; whether they are an admin is decided by the backend,
                which 404s /api/admin/* for everyone else. */}
            <Route path="/admin" element={
              <RequireAuth>
                <Suspense fallback={<div className="flex justify-center py-20"><Spinner /></div>}>
                  <Admin />
                </Suspense>
              </RequireAuth>
            } />
            <Route path="/account" element={
              <RequireAuth><Account /></RequireAuth>
            } />
            {/* Where every "upgrade" link goes (#210 decision 8): the plan grid
                with checkout, for anyone signed in. /pricing is a marketing
                page on the other host and cannot start a checkout. */}
            <Route path="/plans" element={
              <RequireAuth><Plans /></RequireAuth>
            } />
            <Route path="/billing/success" element={
              <RequireAuth><BillingSuccess /></RequireAuth>
            } />
            <Route path="/billing/cancel" element={
              <RequireAuth><BillingCancel /></RequireAuth>
            } />
            {/* Catch-all */}
            <Route path="*" element={<CatchAll />} />
          </Routes>
        </main>
      </div>
      </PublicConfigProvider>
      </HostGuard>
    </BrowserRouter>
  )
}
